import { useEffect, useRef } from 'react';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { ChartBreakout } from '@g17eco/types/insight-custom-dashboard';
import { TableColumnType, type ValueValidation } from '@g17eco/types/universalTracker';
import { handleRouteError } from '../../../logger';

interface Props {
  metric: { code?: string; valueValidation?: ValueValidation };
  value: ChartBreakout | undefined;
  // `undefined` is emitted on explicit clear (X icon in the select).
  onChange: (next: ChartBreakout | undefined) => void;
}

export const BreakoutConfig = ({ metric, value, onChange }: Props) => {
  const tableColumns = metric.valueValidation?.table?.columns ?? [];
  const aggregation = metric.valueValidation?.table?.aggregation;

  const textColumns = tableColumns.filter((col) => col.type === TableColumnType.Text);
  // TableGroupColumn has no schema-level type constraint; if aggregation points at a
  // non-text column, fall back to the first text column (else the Select would be empty).
  // Effect below logs the case so misconfigured metrics surface.
  const aggregationCode = aggregation?.columns[0]?.code;
  const aggregationIsTextColumn =
    aggregationCode !== undefined && textColumns.some((c) => c.code === aggregationCode);
  const defaultBreakoutCode = aggregationIsTextColumn ? aggregationCode : textColumns[0]?.code;

  // Log the fallback once per (aggregationCode, fallback) pair so triage can see which
  // metrics ship a non-text aggregation. Plain truthy/equality check would re-fire on
  // every editor render where the metric is the same.
  const loggedAggregationFallbackRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (aggregationCode === undefined || aggregationIsTextColumn) {
      return;
    }
    const key = `${aggregationCode}|${defaultBreakoutCode ?? ''}`;
    if (loggedAggregationFallbackRef.current === key) {
      return;
    }
    loggedAggregationFallbackRef.current = key;
    handleRouteError(
      new Error(
        `BreakoutConfig: aggregation column "${aggregationCode}" is not a text column on metric "${metric.code ?? '<unknown>'}"; fell back to "${defaultBreakoutCode ?? '<none>'}"`,
      ),
      {
        context: 'BreakoutConfig.nonTextAggregation',
        metricCode: metric.code,
        aggregationCode,
        fallbackCode: defaultBreakoutCode ?? '',
        availableTextColumns: textColumns.map((c) => c.code).join(','),
      },
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregationCode, aggregationIsTextColumn, defaultBreakoutCode]);

  // Per-instance: if BreakoutConfig unmounts (parent toggles to a non-breakout
  // subType then back) the ref resets and the saved selection re-defaults. Lift to
  // a parent ref if this becomes user-visible.
  const lastEmittedBreakoutRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (value !== undefined) {
      lastEmittedBreakoutRef.current = value.columns[0]?.code;
      return;
    }
    if (defaultBreakoutCode === undefined) {
      return;
    }
    // Preserve user selection across metric swaps when the code is still valid; fall back to defaults otherwise.
    const breakoutCode =
      lastEmittedBreakoutRef.current !== undefined &&
      textColumns.some((c) => c.code === lastEmittedBreakoutRef.current)
        ? lastEmittedBreakoutRef.current
        : defaultBreakoutCode;
    onChange({ columns: [{ code: breakoutCode }] });
    lastEmittedBreakoutRef.current = breakoutCode;
    // textColumns is intentionally not a dep — it's a new array each render.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [value === undefined, defaultBreakoutCode]);

  if (textColumns.length === 0) {
    return (
      <div className='mt-3 text-ThemeTextLight'>
        This metric does not have a text column to group by.
      </div>
    );
  }
  if (value === undefined) {
    return null;
  }

  const textOptions = textColumns.map((col) => ({
    value: col.code,
    label: col.name ?? col.code,
  }));

  const selectedBreakout = textOptions.find((opt) => opt.value === value.columns[0]?.code) ?? null;

  return (
    <div data-testid='breakout-config'>
      <p className='mt-3 text-medium'>Group by</p>
      <SelectFactory
        selectType={SelectTypes.SingleSelect}
        placeholder='Select grouping column'
        options={textOptions}
        value={selectedBreakout}
        isClearable
        onChange={(option) => {
          if (option === null) {
            onChange(undefined);
            return;
          }
          if (!option.value) {
            return;
          }
          lastEmittedBreakoutRef.current = option.value;
          onChange({ columns: [{ code: option.value }] });
        }}
      />
    </div>
  );
};
